import { useErrorInlineNotificationAPI } from '@/components/ErrorInlineNotification/ErrorInlineNotification';
import {
  TransactionItemDetailOptionStatus,
  useUpdateTransactionItemDetailOption,
} from '@/context/services/transaction_items/Detail/TransactionItemDetail.service';
import { TransactionDetailOptionFormType } from '@/features/transaction_items/Detail/type';
import { useErrorTrackingSystem } from '@/hooks/useErrorTrackingSystem';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useCallback } from 'react';
import { SubmitHandler, UseFormSetError } from 'react-hook-form';
import { useParams } from 'react-router-dom';

type Props = {
  setValidationError: UseFormSetError<TransactionDetailOptionFormType>;
};

export const useSubmitUpdateTransactionDetailOption = ({
  setValidationError,
}: Props) => {
  const { transaction_item_id } = useParams();
  const { mutateAsync } = useUpdateTransactionItemDetailOption(
    transaction_item_id ?? ''
  );
  const { send } = useErrorTrackingSystem();
  const { t } = useTranslation();
  const notify = useToastAPI();
  const setErrorNotification = useErrorInlineNotificationAPI();
  const onSubmitUpdate: SubmitHandler<{
    id: string;
    form: TransactionDetailOptionFormType;
  }> = useCallback(
    async ({ id, form }) => {
      try {
        const resp = await mutateAsync({
          id: id ?? '',
          data: {
            label: form.label,
            code: form.code,
            is_visible: form.is_visible ?? false,
            priority: form.priority,
          },
        });
        if (resp.status === TransactionItemDetailOptionStatus.SUCCESS) {
          notify.success({
            title: t('transaction_items.detail.options.update.success'),
            duration: 3,
          });
          return true;
        } else if (resp.status === TransactionItemDetailOptionStatus.AUTH) {
          location.href = resp.redirectPath;
        } else if (
          resp.status === TransactionItemDetailOptionStatus.PROXY_LOGIN_ERROR
        ) {
          const errorMessage =
            resp.errors.messages?.join('\n') || t('system_error');
          setErrorNotification(new Error(errorMessage));
          return false;
        } else if (resp.status === TransactionItemDetailOptionStatus.INVALID) {
          Object.entries(resp.errors.errors).forEach(([key, value]) => {
            if (key in form) {
              for (const errorValue of value) {
                const errorMessage =
                  errorValue?.code === 'taken'
                    ? t('transaction_items.detail.options.validation.taken', {
                        name: t(`transaction_items.detail.options.${key}`),
                      })
                    : errorValue?.message;

                setValidationError(
                  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                  key as keyof TransactionDetailOptionFormType,
                  {
                    type: 'validate',
                    message: errorMessage,
                  }
                );
              }
            } else {
              notify.error({
                title: t('transaction_items.detail.options.update.error'),
                duration: 3,
              });
            }
          });
          return false;
        } else {
          notify.error({
            title: t('transaction_items.detail.options.update.error'),
            duration: 3,
          });
          return false;
        }
      } catch (error) {
        if (error instanceof Error && error?.name === 'AxiosError') {
          send(error.message);
        } else {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          send(error as Error);
        }
        notify.error({
          title: t('transaction_items.detail.options.update.error'),
          duration: 3,
        });
        return false;
      }
    },
    [mutateAsync, t, setValidationError, send, notify, setErrorNotification]
  );

  return { onSubmitUpdate };
};
